import { Search } from 'lucide-react';
import { ChangeEvent, FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/Domain/components/Shared/Input/Input';

interface Props {
  query: string;
  setQuery: (value: string) => void;
  // A failed search isn't retried on its own, so Enter on the current search runs it again.
  onSearchAgain: () => void;
}

// Searches on Enter rather than per keystroke: each search is a full-text query over the whole Inbox.
export const ConversationSearch: React.FC<Props> = ({ query, setQuery, onSearchAgain }) => {
  const { t } = useTranslation('inbox_v2');
  const [value, setValue] = useState(query);
  const hintId = useId();

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setValue(nextValue);
    if (query && !nextValue.trim()) {
      setQuery('');
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextQuery = value.trim();
    if (nextQuery && nextQuery === query) {
      onSearchAgain();
    } else {
      setQuery(nextQuery);
    }
  };

  const hasUnsubmittedSearch = value.trim() !== '' && value.trim() !== query;

  return (
    <form role="search" className="animate-fadeIn mb-4.5" onSubmit={handleSubmit}>
      <Input
        type="search"
        enterKeyHint="search"
        placeholder={t('search')}
        size="sm"
        value={value}
        onChange={handleOnChange}
        startIcon={<Search className="size-4 text-foreground-subtle" />}
        aria-describedby={hasUnsubmittedSearch ? hintId : undefined}
        data-testid="conversation-search-input"
      />
      {hasUnsubmittedSearch && (
        <p
          id={hintId}
          className="mt-1.5 text-xs text-muted-foreground"
          data-testid="conversation-search-hint"
        >
          {t('search_hint')}
        </p>
      )}
    </form>
  );
};
